// Krug 5 (K7.2): materials an item gives back when it is dismantled. The weights per item are
// item_salvage.json (by catalog name, all 80 items; 94 since 11. 10. 2026), checked when this module loads, like the perk
// file. For the 32 items with a Workshop recipe the weights are the recipe's material mix.
//
//   U = max(1, round((1 + perIlvl · ilvl) × rarityMult × accessoryMult)), split by the weights
//   with the largest remainder (the heaviest material gets at least 1).
//
// No loop profit (2.4): an item with a recipe of its rarity gives back at most 40% of each
// material of that recipe; a Relic Forge item (account-bound) was made from Relic Shards only,
// so it gives no materials.

import { z } from 'zod';
import { ITEM_TEMPLATES } from '../content/items';
import { RECIPES } from '../content/recipes';
import type { Rarity, StackKind, StackMap } from '../types';
import raw from './item_salvage.json';

export const SALVAGE_MATERIALS = ['metal', 'cloth', 'wood', 'electronics', 'chemicals', 'medicine', 'fuel'] as const satisfies readonly StackKind[];
export type SalvageMaterial = (typeof SALVAGE_MATERIALS)[number];

/** The formula's knobs (the ilvl factor is the one K7.2, 2.5 tunes against `sim:progress`). */
// perIlvl: 0.25 in the spec; with it buildings went up noticeably faster in `sim:progress` (2.5:
// the active Gunner reached Storage 7 about 10 days sooner), so it is 0.15 (BALANCE_REPORT K7).
export const SALVAGE_RULES = { perIlvl: 0.15, recipeCap: 0.4 };

const zWeights = z.partialRecord(z.enum(SALVAGE_MATERIALS), z.number().int().positive());
const zEntry = z.object({ weights: zWeights, accessory: z.boolean().optional() }).strict();
const zFile = z
  .object({
    version: z.number(),
    note: z.string(),
    rarityMult: z.object({ common: z.number(), uncommon: z.number(), rare: z.number(), epic: z.number(), legendary: z.number() }).strict(),
    accessoryMult: z.number().positive(),
    items: z.record(z.string(), zEntry),
  })
  .strict();

export type SalvageFile = z.infer<typeof zFile>;
export interface SalvageDef {
  weights: Partial<Record<SalvageMaterial, number>>;
  accessory: boolean;
}

/**
 * Checks the salvage file: every catalog item exactly once (by name), known materials only, at
 * least one weight, and `accessory` exactly on the accessories. Throws with every problem at once.
 */
export function validateSalvageFile(data: unknown): SalvageFile {
  const parsed = zFile.safeParse(data);
  if (!parsed.success) throw new Error(`item_salvage.json: ${parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`);
  const f = parsed.data;
  const errors: string[] = [];
  const byName = new Map(ITEM_TEMPLATES.map((t) => [t.name, t]));
  for (const name of Object.keys(f.items)) if (!byName.has(name)) errors.push(`"${name}" is not a catalog item`);
  for (const t of ITEM_TEMPLATES) {
    const e = f.items[t.name];
    if (!e) {
      errors.push(`"${t.name}" has no entry`);
      continue;
    }
    if (!Object.keys(e.weights).length) errors.push(`"${t.name}" has no weights`);
    if (!!e.accessory !== (t.slot === 'accessory')) errors.push(`"${t.name}": accessory flag does not match its slot`);
  }
  if (errors.length) throw new Error(`item_salvage.json: ${errors.join('; ')}`);
  return f;
}

export const SALVAGE_FILE: SalvageFile = validateSalvageFile(raw);

/** Salvage weights by template id. */
export const SALVAGE: Record<string, SalvageDef> = Object.fromEntries(
  ITEM_TEMPLATES.map((t) => {
    const e = SALVAGE_FILE.items[t.name];
    return [t.id, { weights: e.weights, accessory: !!e.accessory }];
  }),
);

/** Workshop recipe materials (no Scrap, no Blueprint) by "templateId:rarity". */
const RECIPE_MATERIALS: Record<string, StackMap> = Object.fromEntries(
  RECIPES.filter((r) => r.output.kind === 'item').map((r) => {
    const out = r.output as { templateId: string; rarity: Rarity };
    const mats: StackMap = {};
    for (const [k, v] of Object.entries(r.cost) as [StackKind, number][]) if (v && k !== 'scrap' && k !== 'blueprint') mats[k] = v;
    return [`${out.templateId}:${out.rarity}`, mats];
  }),
);

/** The materials of the Workshop recipe that makes this item in this rarity, if there is one. */
export function recipeMaterials(templateId: string, rarity: Rarity): StackMap | null {
  return RECIPE_MATERIALS[`${templateId}:${rarity}`] ?? null;
}

/**
 * 2. 10. 2026 (package 1, 7.a): a crafted item (`items.source = 'crafted'`) gives no Relic Shards and at most
 * `scrapShare` of its recipe's Scrap when it is dismantled, so crafting an Epic and taking it apart is never
 * a profit (it used to give the full Scrap of an Epic and 1–2 shards). `on: false`: the rule before (sims).
 */
export const CRAFTED_SALVAGE = { on: true, scrapShare: 0.4 };

const RARITY_ORDER: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

/**
 * The most Scrap a crafted item gives back: `scrapShare` of the Scrap of its template's recipe of that rarity,
 * else of the best recipe below it (Foreman's Crown can make a Rare recipe come out Epic). Null without a recipe.
 */
export function craftedScrapCap(templateId: string, rarity: Rarity): number | null {
  const rank = RARITY_ORDER.indexOf(rarity);
  const own = RECIPES.filter((r) => r.output.kind === 'item' && r.output.templateId === templateId && RARITY_ORDER.indexOf(r.output.rarity) <= rank).sort((a, b) => RARITY_ORDER.indexOf((b.output as { rarity: Rarity }).rarity) - RARITY_ORDER.indexOf((a.output as { rarity: Rarity }).rarity));
  const r = own[0];
  return r ? Math.round((r.cost.scrap ?? 0) * CRAFTED_SALVAGE.scrapShare) : null;
}

/** Units of material an item gives: U in the formula above. */
export function salvageUnits(item: { templateId: string; rarity: Rarity; ilvl: number }): number {
  const def = SALVAGE[item.templateId];
  const acc = def?.accessory ? SALVAGE_FILE.accessoryMult : 1;
  return Math.max(1, Math.round((1 + SALVAGE_RULES.perIlvl * item.ilvl) * SALVAGE_FILE.rarityMult[item.rarity] * acc));
}

/** Splits `units` by the weights with the largest remainder; the sum is exactly `units`. */
export function splitByWeights(units: number, weights: Partial<Record<string, number>>): Record<string, number> {
  const entries = Object.entries(weights).filter((e): e is [string, number] => (e[1] ?? 0) > 0);
  const total = entries.reduce((a, [, w]) => a + w, 0);
  if (!entries.length || units <= 0 || total <= 0) return {};
  // Heavier first, so a tie goes to the heavier material (and then to the one listed first).
  const order = entries.map(([k, w], i) => ({ k, w, i, q: (units * w) / total })).sort((a, b) => b.w - a.w || a.i - b.i);
  const out: Record<string, number> = Object.fromEntries(order.map((x) => [x.k, Math.floor(x.q)]));
  let left = units - order.reduce((a, x) => a + Math.floor(x.q), 0);
  for (const x of [...order].sort((a, b) => b.q - Math.floor(b.q) - (a.q - Math.floor(a.q)) || b.w - a.w || a.i - b.i)) {
    if (left <= 0) break;
    out[x.k]++;
    left--;
  }
  // The heaviest material always gets at least one (with units ≥ 1 the rule above already does).
  const top = order[0].k;
  if (out[top] < 1) {
    const donor = order.find((x) => x.k !== top && out[x.k] > 0);
    if (donor) {
      out[donor.k]--;
      out[top]++;
    }
  }
  for (const k of Object.keys(out)) if (!out[k]) delete out[k];
  return out;
}

/** Materials returned on dismantling (without the upgrade refund, Scrap or Relic Shards). */
export function salvageMaterials(item: { templateId: string; rarity: Rarity; ilvl: number; accountBound?: boolean }): StackMap {
  const def = SALVAGE[item.templateId];
  if (!def) return {};
  // A Relic Forge item was made from Relic Shards alone: 40% of no materials is none.
  if (item.accountBound) return {};
  const out = splitByWeights(salvageUnits(item), def.weights) as StackMap;
  const recipe = recipeMaterials(item.templateId, item.rarity);
  if (recipe) {
    for (const k of Object.keys(out) as StackKind[]) {
      const cap = Math.floor((recipe[k] ?? 0) * SALVAGE_RULES.recipeCap);
      out[k] = Math.min(out[k] ?? 0, cap);
      if (!out[k]) delete out[k];
    }
  }
  return out;
}
